import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Library, BookOpen, Feather, Leaf, Star, Heart, Compass, Globe, Music, Palette, Camera, Coffee, Moon, Sun, Mountain, Anchor,
  Atom, Brain, Briefcase, Baby, GraduationCap, Landmark, Scale, Microscope, Rocket, Sparkles, Flame, Gem, Map, Trees,
  Utensils, Drama, Gamepad2, Puzzle, Lightbulb, PenTool, Quote, Scroll, Shield, Sword, Ghost, Flower2, Bird, Cat, Dog,
  Fish, Plane, Ship, Home, Clock, Crown, Award, Hourglass, Telescope, Stethoscope, Dumbbell, Church, Castle, Waves, Snowflake
} from 'lucide-react';

export const COLLECTION_ICONS = {
  library: Library, book: BookOpen, feather: Feather, leaf: Leaf, star: Star, heart: Heart, compass: Compass, globe: Globe,
  music: Music, palette: Palette, camera: Camera, coffee: Coffee, moon: Moon, sun: Sun, mountain: Mountain, anchor: Anchor,
  atom: Atom, brain: Brain, briefcase: Briefcase, baby: Baby, graduation: GraduationCap, landmark: Landmark, scale: Scale,
  microscope: Microscope, rocket: Rocket, sparkles: Sparkles, flame: Flame, gem: Gem, map: Map, trees: Trees, utensils: Utensils,
  drama: Drama, game: Gamepad2, puzzle: Puzzle, lightbulb: Lightbulb, pen: PenTool, quote: Quote, scroll: Scroll, shield: Shield,
  sword: Sword, ghost: Ghost, flower: Flower2, bird: Bird, cat: Cat, dog: Dog, fish: Fish, plane: Plane, ship: Ship, home: Home,
  clock: Clock, crown: Crown, award: Award, hourglass: Hourglass, telescope: Telescope, stethoscope: Stethoscope, dumbbell: Dumbbell,
  church: Church, castle: Castle, waves: Waves, snowflake: Snowflake
};

export const GERAL = { id: '__geral__', nome: 'Geral', icon: 'library' };

export const collectionSlug = (nome) => (nome || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export const collectionLink = (baseUrl, nome) => `${baseUrl}?colecao=${collectionSlug(nome)}`;

export const CollectionIcon = ({ name, className = 'w-4 h-4', style }) => {
  const Icon = COLLECTION_ICONS[name] || Library;
  return <Icon className={className} style={style} strokeWidth={1.5} />;
};

export const bookCollections = (book, collections) => {
  const byId = new window.Map(collections.map((c) => [c.id, c]));
  const list = (book.colecoes || []).map((id) => byId.get(id)).filter(Boolean);
  return list.length ? list : [GERAL];
};

export const IconGallery = ({ value, onChange }) => (
  <div data-testid="collection-icon-gallery" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(38px, 1fr))', gap: 6, maxHeight: 180, overflowY: 'auto', padding: 4 }}>
    {Object.keys(COLLECTION_ICONS).map((k) => (
      <button key={k} type="button" onClick={() => onChange(k)} title={k} data-testid={`collection-icon-${k}`}
        style={{ height: 38, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `1px solid ${value === k ? '#68AAB0' : '#e2e8f0'}`, background: value === k ? '#e6f6f7' : 'white', color: '#2d3748' }}>
        <CollectionIcon name={k} className="w-5 h-5" />
      </button>
    ))}
  </div>
);

// Multi-select chips used in the book form
export const CollectionsPicker = ({ collections, value = [], onChange }) => {
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  if (!collections.length) return <p data-testid="collections-picker-empty" style={{ fontSize: '0.8rem', color: '#718096' }}>Sem coleções criadas: o livro fica em "Geral". Crie coleções no menu Coleções.</p>;
  return (
    <div data-testid="collections-picker" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {collections.map((c) => {
        const on = value.includes(c.id);
        return (
          <button key={c.id} type="button" onClick={() => toggle(c.id)} data-testid={`collections-picker-${c.id}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.3rem 0.7rem', borderRadius: 999, cursor: 'pointer', fontSize: '0.82rem',
              border: `1px solid ${on ? '#68AAB0' : '#cbd5e0'}`, background: on ? '#68AAB0' : 'white', color: on ? 'white' : '#4a5568' }}>
            <CollectionIcon name={c.icon} className="w-3.5 h-3.5" />{c.nome}
          </button>
        );
      })}
      {!value.length && <span style={{ fontSize: '0.78rem', color: '#718096', alignSelf: 'center' }}>Nenhuma marcada: fica em "Geral"</span>}
    </div>
  );
};

// Tracks whether a horizontal scroller can still move left/right
const useScrollEdges = (dep) => {
  const ref = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setEdges({ left: el.scrollLeft > 2, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2 });
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', update); ro.disconnect(); };
  }, [dep]);
  return [ref, edges];
};

// Strip in the public catalog (same tint as "Continue lendo")
export const CollectionsBar = ({ collections, books, selected, onSelect, color }) => {
  const hasGeral = books.some((b) => !(b.colecoes || []).some((id) => collections.some((c) => c.id === id)));
  const items = [...(hasGeral ? [GERAL] : []), ...collections.filter((c) => books.some((b) => (b.colecoes || []).includes(c.id)))];
  const [ref, edges] = useScrollEdges(items.length);
  if (items.length < 2 && !collections.length) return null;
  const chip = (c, label, icon, testId) => {
    const on = selected === c;
    return (
      <button key={testId} type="button" onClick={() => onSelect(on ? null : c)} data-testid={testId}
        style={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.45rem 0.95rem', borderRadius: 999, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
          border: `1px solid ${on ? color : `color-mix(in srgb, ${color} 35%, white)`}`, background: on ? color : 'white', color: on ? 'white' : '#2d3748', transition: 'background-color 0.2s ease, color 0.2s ease' }}>
        {icon}{label}
      </button>
    );
  };
  const arrow = (side) => edges[side] && (
    <button type="button" className={`collections-arrow collections-arrow-${side}`} aria-label={side === 'left' ? 'Ver coleções anteriores' : 'Ver mais coleções'}
      onClick={() => ref.current.scrollBy({ left: (side === 'left' ? -1 : 1) * ref.current.clientWidth * 0.7 })} data-testid={`collections-arrow-${side}`}
      style={{ border: `1px solid color-mix(in srgb, ${color} 35%, white)`, color }}>
      {side === 'left' ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
    </button>
  );
  return (
    <section className="collections-bar" data-testid="catalog-collections-bar" style={{ position: 'relative', background: `color-mix(in srgb, ${color} 12%, white)`, border: `1px solid color-mix(in srgb, ${color} 30%, white)`, borderRadius: 12, padding: '0.75rem 1rem', margin: '0 0 1.25rem' }}>
      {arrow('left')}
      <div ref={ref} className="continue-scroll collections-scroll" data-testid="collections-bar-scroll" style={{ '--cr-color': color, display: 'flex', alignItems: 'center', gap: 8, overflowX: 'auto' }}>
        {chip(null, 'Todos', <Library className="w-4 h-4" strokeWidth={1.5} />, 'collections-bar-all')}
        {items.map((c) => chip(c.id, c.nome, <CollectionIcon name={c.icon} />, `collections-bar-${c.id}`))}
      </div>
      {arrow('right')}
    </section>
  );
};
